

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Grid,
  Modal,
  Box,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import { useAuth } from 'auth/Auth';
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import Icon from "@mui/material/Icon";
import ProductForm from 'components/NewProductForm/NewProductForm';
import { formatDate } from 'utils/DateTimeConversion';
import CircularProgress from '@mui/material/CircularProgress';
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import MonoText from 'components/MonoText';
import { usePermission } from 'auth/usePermission';
import { frontendLogger } from '../../utils/logger';
import colors from "assets/theme/base/colors";
import TableActionMenu from "components/TableActionMenu";

const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== 'string' || !str.trim()) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export default function Inventory() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { user, axiosInstance } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true); // For table loading
  const [filteredProducts, setFilteredProducts] = useState([]);
  const { canCreate, canDelete, isSupportUser } = usePermission('products');

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    product: null,
    loading: false,
  });

  const handleOpenModal = () => {
    setIsButtonLoading(true);
    setIsModalOpen(true);
    setIsButtonLoading(false); // Reset immediately after modal opens
  };
  const handleCloseModal = () => setIsModalOpen(false);

  useEffect(() => {

    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    console.log("Fetching inventory...");
    setIsTableLoading(true); // Start table loading
    try {
      // First fetch products
      const productsResponse = await axiosInstance.get('/products/');
      const products = productsResponse.data;
      console.log("products:", products);

      // Then fetch inventory data
      const inventoryResponse = await axiosInstance.get('/inventory/');
      const inventoryData = inventoryResponse.data;
      console.log("inventory:", inventoryData);

      // Merge product and inventory data
      const mergedData = products.map(product => {
        // Get all inventory records for this product
        const inventoryItems = inventoryData.filter(item => item.product_id === product.id);

        // Logic to pick the desired inventory record
        // For example, pick the latest inventory record based on `created_at`
        const inventoryItem = inventoryItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || {};

        // Spread the product first so every filterable field (name, product_code,
        // sku, price, brand, cf1..) stays on the row; aliases below are for display
        return {
          ...product,
          id: product.id,
          product_id: product.id,
          product_name: product.name,
          quantity: inventoryItem.quantity || 0,
          location: inventoryItem.location ? inventoryItem.location.trim() : '',
          created_at: product.created_at, // Ensure this is included
          updated_at: inventoryItem.updated_at || product.updated_at,
          currency: product.currency || 'USD',
        };
      });

      setInventory(mergedData);
      setFilteredProducts(mergedData);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setIsTableLoading(false); // Stop table loading
    }
  };

  const handleRowClick = (rowData) => {
    const id = rowData?.product_id || rowData?.id;
    if (id) {
      navigate(`/products/${id}`);
    }
  };

  const handleDeleteClick = (productOrId) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to delete products.',
        severity: 'error',
      });
      return;
    }
    const product = typeof productOrId === 'object' && productOrId !== null
      ? productOrId
      : inventory.find((p) => (p.product_id || p.id) === productOrId) || { id: productOrId, product_id: productOrId };

    setDeleteDialog({
      open: true,
      product,
      loading: false,
    });
  };

  const handleDeleteCancel = () => {
    if (deleteDialog.loading) return;
    setDeleteDialog({ open: false, product: null, loading: false });
  };

  const handleDeleteConfirm = async () => {
    const { product } = deleteDialog;
    if (!product) return;
    const productId = product.product_id || product.id;

    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to delete products.',
        severity: 'error',
      });
      return;
    }

    setDeleteDialog((d) => ({ ...d, loading: true }));
    try {
      await axiosInstance.delete(`/products/${productId}`);

      try {
        await frontendLogger.logDelete('Product', {
          id: productId,
          name: product.product_name || product.name,
          deleted_by: user?.id,
          user: user?.username,
          timestamp: new Date().toISOString(),
        });
      } catch (logError) {
        console.error('Error logging product deletion:', logError);
      }

      setInventory((prev) => prev.filter((item) => (item.product_id || item.id) !== productId));
      setFilteredProducts((prev) => prev.filter((item) => (item.product_id || item.id) !== productId));

      setDeleteDialog({ open: false, product: null, loading: false });
      setSnackbar({
        open: true,
        message: 'Product deleted successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting product:', error);
      const detail = error.response?.data?.detail;
      setSnackbar({
        open: true,
        message: typeof detail === 'string' ? detail : 'Error deleting product. Please try again.',
        severity: error.response?.status === 400 ? 'warning' : 'error',
      });
      setDeleteDialog((d) => ({ ...d, loading: false }));
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const inventoryTableData = useMemo(() => ({
    columns: [
      {
        Header: "ID",
        accessor: "product_id",
        width: "5%",
        Cell: ({ row }) => (
          <MDTypography
            component="span"
            onClick={() => handleRowClick(row.original)}
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{ cursor: 'pointer' }}
          >
            {`#${row.original.product_id || row.original.id}`}
          </MDTypography>
        ),
      },
      {
        Header: "Name",
        accessor: "product_name",
        width: "20%",
        Cell: ({ value, row }) => (
          <MDTypography
            component="span"
            onClick={() => handleRowClick(row.original)}
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{
              cursor: 'pointer',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.35,
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            {capitalizeFirstLetter(value) || '-'}
          </MDTypography>
        ),
      },
      {
        Header: "Category",
        accessor: "category",
        width: "14%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {capitalizeFirstLetter(value) || "-"}
          </MDTypography>
        ),
      },
      {
        Header: "Code",
        accessor: "product_code",
        width: "10%",
        Cell: ({ value }) => <MonoText>{value || '-'}</MonoText>,
      },
      {
        Header: "Quantity",
        accessor: "quantity",
        width: "8%",
        Cell: ({ value, row }) => {
          const productType = row.original.product_type;

          if (productType === 'Service' || productType === 'Digital') {
            return (
              <Tooltip title="No stock tracking" arrow>
                <MDTypography
                  component="span"
                  variant="caption"
                  color="secondary"
                  fontWeight="medium"
                  sx={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontStyle: 'italic',
                    cursor: 'help' // Changes cursor to indicate this is interactive
                  }}
                >
                  -
                </MDTypography>
              </Tooltip>
            );
          }

          return (
            <MDTypography
              component="span"
              variant="caption"
              color="text"
              fontWeight="medium"
              sx={{
                backgroundColor: value < 1 ? '#ffcccb' : '#90EE90',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
            >
              {value != null ? value : 0}
            </MDTypography>
          );
        }
      },
      {
        Header: "Gross Price",
        accessor: "final_unit_price",
        width: "10%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {value != null && value !== '' ? value : '-'}
          </MDTypography>
        ),
      },
      {
        Header: "Currency",
        accessor: "currency",
        width: "8%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {value || '-'}
          </MDTypography>
        ),
      },

      {
        Header: "Location",
        accessor: "location",
        width: "12%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {capitalizeFirstLetter(value) || '-'}
          </MDTypography>
        ),
      },
      {
        Header: "Last Updated",
        accessor: "updated_at",
        width: "15%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {value ? formatDate(value, 'datetime') : '-'}
          </MDTypography>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "5%",
        align: "center",
        disableSortBy: true,
        disableExport: true,
        Cell: ({ row }) => (
          canDelete ? (
            <TableActionMenu
              row={row.original}
              onView={handleRowClick}
              onDelete={() => handleDeleteClick(row.original)}
            />
          ) : (
            <TableActionMenu
              row={row.original}
              onView={handleRowClick}
            />
          )
        ),
      },
    ],
    rows: inventory,
  }), [inventory, canDelete]);


  // Inside the Inventory component, after fetching inventory
  // Filter inventory items by date (created_at or updated_at)
  const filterInventoryByDate = (inventory, days) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return inventory.filter(item => {
      const itemDate = new Date(item.created_at || item.updated_at);
      return itemDate >= cutoffDate;
    });
  };

  // Calculate percentage change between current and previous period
  // Calculate percentage change between current and previous period
  const calculatePercentageChange = (current, previous) => {
    console.log(`Calculating change: current=${current}, previous=${previous}`);

    if (previous === 0) {
      if (current === 0) return { amount: "0%", color: "info" };
      return { amount: "+100%", color: "success" }; // Any increase from 0 is treated as 100%
    }

    const change = ((current - previous) / previous) * 100;
    return {
      amount: `${change >= 0 ? '+' : ''}${Math.abs(change).toFixed(1)}%`,
      color: change >= 0 ? "success" : "error",
    };
  };

  // Total Products (last 30 days vs. previous 30 days)
  const currentMonthProducts = filterInventoryByDate(inventory, 30).length;
  const previousMonthProducts = filterInventoryByDate(
    inventory.filter(item => {
      const itemDate = new Date(item.created_at || item.updated_at);
      const past30Days = new Date();
      past30Days.setDate(past30Days.getDate() - 30);
      const past60Days = new Date();
      past60Days.setDate(past60Days.getDate() - 60);
      return itemDate >= past60Days && itemDate < past30Days;
    }),
    30
  ).length;
  const totalProductsChange = calculatePercentageChange(currentMonthProducts, previousMonthProducts);

  // Low Stock Items (last 7 days vs. previous 7 days)
  const currentWeekLowStock = filterInventoryByDate(inventory, 7).filter(item => item.quantity <= 0).length;
  const previousWeekLowStock = filterInventoryByDate(
    inventory.filter(item => {
      const itemDate = new Date(item.updated_at); // Use updated_at for stock changes
      const past7Days = new Date();
      past7Days.setDate(past7Days.getDate() - 7);
      const past14Days = new Date();
      past14Days.setDate(past14Days.getDate() - 14);
      return itemDate >= past14Days && itemDate < past7Days;
    }),
    7
  ).filter(item => item.quantity <= 0).length;
  const lowStockChange = calculatePercentageChange(currentWeekLowStock, previousWeekLowStock);

  // Total Inventory Value (today vs. yesterday)
  const todayValue = inventory.reduce((sum, item) => sum + (item.quantity * item.final_unit_price), 0);
  const yesterdayValue = filterInventoryByDate(inventory, 1).reduce(
    (sum, item) => sum + (item.quantity * item.final_unit_price), 0
  );
  const valueChange = calculatePercentageChange(todayValue, yesterdayValue);

  // Product Categories (current vs. last quarter)
  const currentCategories = new Set(inventory.map(item => item.category)).size;
  const lastQuarterCategories = new Set(
    filterInventoryByDate(inventory, 90).map(item => item.category)
  ).size;
  const categoriesChange = calculatePercentageChange(currentCategories, lastQuarterCategories);

  // Helper function to filter by date range
  // Helper function to filter by date range
  const filterByDateRange = (data, startDays, endDays = 0) => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - endDays);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - startDays);

    console.log(`Filtering from ${startDate.toISOString()} to ${endDate.toISOString()}`);

    return data.filter(item => {
      const itemDate = new Date(item.created_at || item.updated_at);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const [metrics, setMetrics] = useState({
    productGrowth: { amount: "0%", color: "info" },
    stockChange: { amount: "0%", color: "info" },
    valueChange: { amount: "0%", color: "info" },
    categoryGrowth: { amount: "0%", color: "info" },
    currentMonthProducts: 0
  });

  // Calculate metrics when inventory changes
  // In your useEffect where metrics are calculated:
  useEffect(() => {
    if (inventory && inventory.length > 0) {
      // Calculate today's date and yesterday's date for proper filtering
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);

      const twoWeeksAgo = new Date(today);
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

      const lastMonth = new Date(today);
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      // 1. Total Products - Compare with last month only
      const totalProducts = inventory.length;
      const lastMonthProducts = inventory.filter(item => {
        const createdDate = new Date(item.created_at);
        return createdDate < lastMonth;
      }).length;

      const newProducts = totalProducts - lastMonthProducts;
      const productGrowth = {
        amount: `${newProducts} new`,
        color: newProducts >= 0 ? "success" : "error",
        label: "since last month"
      };

      // 2. Low Stock Items - Compare with last week
      const currentLowStock = inventory.filter(p => p.quantity <= 0).length;
      const lastWeekLowStock = inventory.filter(p => {
        const updatedDate = new Date(p.updated_at);
        return updatedDate < lastWeek && p.quantity <= 0;
      }).length;

      const lowStockChange = ((currentLowStock - lastWeekLowStock) / (lastWeekLowStock || 1)) * 100;
      const stockChange = {
        color: lowStockChange <= 0 ? "success" : "error", // Fewer low stock items is good
        amount: `${lowStockChange.toFixed(1)}%`,
        label: "than last week"
      };

      // 3. Total Value - Compare with yesterday
      const currentValue = inventory.reduce((sum, p) => sum + (p.quantity * p.final_unit_price), 0);
      const yesterdayValue = inventory.filter(p => {
        const updatedDate = new Date(p.updated_at);
        return updatedDate < yesterday;
      }).reduce((sum, p) => sum + (p.quantity * p.final_unit_price), 0);

      const valueChangePercent = ((currentValue - yesterdayValue) / (yesterdayValue || 1)) * 100;
      const valueChange = {
        color: valueChangePercent >= 0 ? "success" : "error",
        amount: `${valueChangePercent > 0 ? '+' : ''}${valueChangePercent.toFixed(1)}%`,
        label: "than yesterday"
      };

      setMetrics({
        productGrowth,
        stockChange,
        valueChange,
        currentMonthProducts: newProducts
      });
    }
  }, [inventory]);

  // Step 3: Implement filtering logic
  const filterProducts = (criteria) => {
    const filtered = inventory.filter(product => {
      // Implement your filtering logic based on criteria
      return true; // Replace with actual condition
    });
    setFilteredProducts(filtered);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {/* Total Products - Shows new products added this month */}
          {/* Total Products */}
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="dark"
              icon="inventory_2"
              title="Total Products"
              count={inventory.length}
              percentage={{
                color: metrics.productGrowth.color,
                amount: metrics.productGrowth.amount,
                label: capitalizeFirstLetter(metrics.productGrowth.label)
              }}
            />
          </Grid>

          {/* Low Stock Items */}
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              icon="warning"
              title="Low Stock Items"
              count={inventory.filter(p => p.quantity <= 0).length}
              percentage={{
                color: metrics.stockChange.color,
                amount: metrics.stockChange.amount,
                label: capitalizeFirstLetter(metrics.stockChange.label)
              }}
            />
          </Grid>

          {/* Total Inventory Value */}
          {/* <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="success"
              icon="attach_money"
              title="Total Value"
              count={new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR'
              }).format(
                inventory.reduce((sum, p) => sum + (p.quantity * p.final_unit_price), 0)
              )}
              percentage={{
                color: metrics.valueChange.color,
                amount: metrics.valueChange.amount,
                label: capitalizeFirstLetter(metrics.valueChange.label)
              }}
            />
          </Grid> */}

          {/* Product Categories - No percentage */}
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="primary"
              icon="category"
              title="Product Categories"
              count={new Set(inventory.map(p => capitalizeFirstLetter(p.category))).size}
            />
          </Grid>
        </Grid>

        <MDBox mt={4.5}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Products
                  </MDTypography>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={() => {
                      setIsLoading(true);
                      handleOpenModal();
                      // Add small delay to show spinner before modal opens
                      setTimeout(() => setIsLoading(false), 300);
                    }}
                    title="Click to add a new product to the inventory"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        &nbsp;Add Product
                      </>
                    )}
                  </MDButton>
                </MDBox>
                <MDBox pt={3}>
                  {isTableLoading ? (
                    <TableSkeleton />
                  ) : (
                    <>
                      {filteredProducts.length > 0 ? (
                        <DataTable
                          table={inventoryTableData}
                          isSorted={true}
                          noEndBorder
                          canSearch
                          searchPlaceholder="Search for products..."
                          entriesPerPage={{
                            defaultValue: 10,
                            entries: [5, 10, 15, 20, 25],
                          }}
                          showTotalEntries
                          pagination={{ variant: "gradient", color: "info" }}
                          exportCSV={true}
                          objectType="product"
                          onFilterApply={(conditions) => {
                            console.log('Filter applied:', conditions);
                            // Filtering is handled by DataTable component
                          }}
                          onFilterClear={() => {
                            console.log('Filter cleared');
                            fetchInventory();
                          }}
                        />
                      ) : (
                        <EmptyState
                          moduleName="Product"
                          canCreate={canCreate}
                        />
                      )}
                    </>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      

      {/* Modal for adding new product */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
          maxWidth: 800,
          maxHeight: '90vh', // Limit maximum height
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Hide overflow
        }}>
          <ProductForm
            handleCloseModal={handleCloseModal}
            onProductCreated={() => {
              fetchInventory();
            }}
          />
        </Box>
      </Modal>

      {/* Delete Product Confirmation Modal */}
      <Modal
        open={deleteDialog.open}
        onClose={deleteDialog.loading ? undefined : handleDeleteCancel}
        aria-labelledby="delete-product-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <Box className="pearl-popup-header" sx={{ backgroundColor: colors.popupHeader.background, px: 4, py: 3 }}>
            <MDTypography id="delete-product-title" variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
              Delete Product
            </MDTypography>
          </Box>

          <Box sx={{ p: 4 }}>
            <MDTypography variant="body1" mb={3} sx={{ color: '#34495e' }}>
              Are you sure you want to delete{' '}
              <strong>{deleteDialog.product?.product_name || deleteDialog.product?.name || `Product #${deleteDialog.product?.product_id ?? deleteDialog.product?.id ?? ''}`}</strong>?
              This action cannot be undone.
            </MDTypography>

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                onClick={handleDeleteCancel}
                variant="outlined"
                color="secondary"
                disabled={deleteDialog.loading}
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDeleteConfirm}
                variant="gradient"
                color="error"
                disabled={deleteDialog.loading}
              >
                {deleteDialog.loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'Delete'
                )}
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </DashboardLayout>
  );
}